import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lightbulb, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { allCategories, catLabel } from "@/lib/lore";
import { useInvalidate } from "@/lib/queries";

function SuggestionRow({ s, uid, onOpen }) {
  const invalidate = useInvalidate();
  const [name, setName] = useState(s.name);
  const [category, setCategory] = useState(s.category);
  const [busy, setBusy] = useState(false);
  const refresh = () => invalidate(["suggestions", uid], ["entities", uid], "entity");

  const accept = async (asStub) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/suggestions/${s.id}/accept`, { name, category, summary: s.summary, as_stub: asStub });
      toast.success(`${data.name} created${asStub ? " as a stub" : ""}`, { description: `Linked to ${data.linked} ${data.linked === 1 ? "entry" : "entries"}`, action: { label: "Open", onClick: () => onOpen(data.id) } });
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const dismiss = async () => {
    await api.delete(`/suggestions/${s.id}`);
    toast.success(`“${s.name}” won't be suggested again`, { description: "You can restore it in Universe settings → Dismissed suggestions." });
    refresh();
  };

  return (
    <motion.div layout initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, x: 20 }}
      className="grid gap-3 rounded-md border border-[#2D3442] bg-[#0F131A] p-4 lg:grid-cols-[1fr_auto]" data-testid={`suggestion-${s.id}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          <Input className="h-9 w-56 font-display text-lg" value={name} onChange={(e) => setName(e.target.value)} data-testid={`suggestion-name-${s.id}`} />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-9 w-44" data-testid={`suggestion-category-${s.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {s.summary && <p className="mt-2 text-sm text-[#E8DFD1]">{s.summary}</p>}
        {s.mentioned_in.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-[#A8A090]">Mentioned in
            {s.mentioned_in.map((m) => <button key={m.id} onClick={() => onOpen(m.id)} className="underline underline-offset-2 hover:text-[#F59E0B]" data-testid={`suggestion-source-${s.id}-${m.id}`}>{m.name}</button>)}
          </div>
        )}
      </div>
      <div className="flex items-start gap-1.5">
        <Button size="sm" onClick={() => accept(false)} disabled={busy || !name.trim()} data-testid={`suggestion-create-${s.id}`}>Create entry</Button>
        <Button size="sm" variant="outline" onClick={() => accept(true)} disabled={busy || !name.trim()} data-testid={`suggestion-stub-${s.id}`}>Stub</Button>
        <Button size="icon" variant="ghost" className="h-9 w-9" onClick={dismiss} disabled={busy} title="Dismiss. It won't be suggested again" data-testid={`suggestion-dismiss-${s.id}`}><X className="h-4 w-4" /></Button>
      </div>
    </motion.div>
  );
}

export const MissingEntriesPanel = ({ uid, onOpen }) => {
  const [open, setOpen] = useState(true);
  const { data: sugg = [] } = useQuery({ queryKey: ["suggestions", uid], queryFn: () => api.get(`/universes/${uid}/suggestions`).then((r) => r.data) });
  if (!sugg.length) return null;
  return (
    <section className="mb-8 rounded-md border border-[#D97706]/30 bg-[#D97706]/5" data-testid="missing-entries-panel">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center justify-between px-5 py-3.5 text-left" data-testid="missing-entries-toggle">
        <span className="flex items-center gap-2 text-sm text-[#F3EBDD]">
          <Lightbulb className="h-4 w-4 text-[#F59E0B]" />
          <b className="font-display text-xl font-normal">{sugg.length} {sugg.length === 1 ? "thing is" : "things are"} mentioned without an entry of their own</b>
        </span>
        <ChevronDown className={`h-4 w-4 text-[#A8A090] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-2 px-5 pb-5" data-testid="missing-entries-list">
          <AnimatePresence>{sugg.map((s) => <SuggestionRow key={s.id} s={s} uid={uid} onOpen={onOpen} />)}</AnimatePresence>
        </div>
      )}
    </section>
  );
};
